import React, { useEffect, useRef, useState } from "react";
import { StyledHeader } from "./styles";
import { Button, Dropdown } from "antd";
import logo from "../../../../images/Group.svg";
import { MenuOutlined, LoginOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import {
  ActionsIcon,
  AssetIcon,
  ChevronIcon,
  DailyLogsIcon,
  DrawingsIcon,
  FormsIcon,
  LocationsIcon,
  PunchIcon,
  RfiIcon,
  SubmittalsIcon,
  TimeIcon
} from "../Home2026/icons";

const platformLinks = [
  { icon: DrawingsIcon, label: "Drawings", blurb: "Always-current sets with markup", to: "/features/drawings" },
  { icon: RfiIcon, label: "RFIs", blurb: "Route questions to answers fast", to: "/features/rfi" },
  { icon: SubmittalsIcon, label: "Submittals", blurb: "Approvals that track themselves", to: "/features/submittals" },
  { icon: DailyLogsIcon, label: "Daily Logs", blurb: "Two-minute field reporting", to: "/features/daily_log" },
  { icon: PunchIcon, label: "Punch Lists", blurb: "Photo proof to closeout", to: "/features/punchlists" },
  { icon: LocationsIcon, label: "Locations & QR", blurb: "QR-coded site areas", to: "/features/locations" },
  { icon: TimeIcon, label: "Time Tracking", blurb: "Crew hours from the field", to: "/features/time_tracking" },
  { icon: AssetIcon, label: "Asset Tracking", blurb: "Tools and equipment on record", to: "/features/asset_tracking" },
  { icon: ActionsIcon, label: "Action Items", blurb: "To-dos with owners and dates", to: "/features/actions" },
  { icon: FormsIcon, label: "Forms", blurb: "Inspections and checklists", to: "/features/forms" }
];

const HeaderMenuItems = [
  {
    key: "features",
    label: <Link to="/features">Platform</Link>
  },
  {
    key: "pricing",
    label: <Link to="/pricing">Pricing</Link>
  },
  {
    key: "about-us",
    label: <Link to="/about_us">About</Link>
  },
  {
    key: "contact-us",
    label: <Link to="/contact_us">Contact</Link>
  },
  {
    key: "safetyloop",
    label: (
      <a href="https://www.safetyloop.com" rel="noreferrer" target="_blank">
        SafetyLoop
      </a>
    )
  },
  {
    type: "divider"
  },
  {
    key: "try-free",
    label: <Link to="/signup">Start free trial</Link>
  },
  {
    key: "login",
    label: <Link to="/login">Log in</Link>,
    icon: <LoginOutlined />
  }
];

export default function Header({ showHeaderMenu = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  const closeTimer = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled((window.scrollY || 0) > 8);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Close the platform panel on navigation
  useEffect(() => {
    setPlatformOpen(false);
  }, [location.pathname]);

  const openPanel = () => {
    window.clearTimeout(closeTimer.current);
    setPlatformOpen(true);
  };

  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setPlatformOpen(false), 140);
  };

  return (
    <StyledHeader className={scrolled ? "is-scrolled" : ""}>
      <div className="header-inner">
        <div className="logo">
          <Link to="/">
            <img src={logo} alt="ProjectLoop" />
          </Link>
        </div>
        {showHeaderMenu && (
          <>
            <nav className="header-nav is-not-mobile" aria-label="Main">
              <div
                className={`nav-platform${platformOpen ? " is-open" : ""}`}
                onMouseEnter={openPanel}
                onMouseLeave={scheduleClose}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setPlatformOpen(false);
                }}
              >
                <button
                  type="button"
                  className="nav-platform__trigger"
                  aria-expanded={platformOpen}
                  aria-haspopup="true"
                  onClick={() => setPlatformOpen((open) => !open)}
                >
                  Platform
                  <span className="nav-platform__caret" aria-hidden="true">
                    <ChevronIcon />
                  </span>
                </button>
                <div className="nav-platform__panel">
                  <div className="nav-platform__grid">
                    {platformLinks.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          className="nav-platform__item"
                          to={item.to}
                          key={item.label}
                        >
                          <span className="nav-platform__icon" aria-hidden="true">
                            <Icon />
                          </span>
                          <span>
                            <strong>{item.label}</strong>
                            <small>{item.blurb}</small>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                  <div className="nav-platform__aside">
                    <div className="nav-platform__cta">
                      <strong>90 days free. No card. No commitment.</strong>
                      <p>Every tool, on every plan, with unlimited subcontractor accounts.</p>
                      <Link to="/signup">
                        Start free trial <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                    <Link className="nav-platform__all" to="/features">
                      All features <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                  <p className="nav-platform__foot">11 tools · Every plan · No add-ons</p>
                </div>
              </div>
              <Link to="/pricing">Pricing</Link>
              <Link to="/about_us">About</Link>
              <Link to="/contact_us">Contact</Link>
              <a href="https://www.safetyloop.com" rel="noreferrer" target="_blank">
                SafetyLoop
              </a>
            </nav>
            <div className="header-actions is-not-mobile">
              <Link to="/login" className="login-link">
                Log in
              </Link>
              <Link to="/signup">
                <Button type="primary" className="try-btn">
                  Start free trial
                </Button>
              </Link>
            </div>
            <Dropdown
              className="is-mobile"
              menu={{
                items: HeaderMenuItems
              }}
              overlayStyle={{
                width: "200px"
              }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="primary" icon={<MenuOutlined />} />
            </Dropdown>
          </>
        )}
      </div>
    </StyledHeader>
  );
}
